import Api from 'services/PtmApi';
import { LoginService, Session } from 'services/LoginService';
import { UserData } from 'models/UserData';
import { UserCreateData } from 'models/UserCreateData';
import { ApiError, returnApiError } from 'models/ApiError';
import { HttpStatusCode } from 'models/HttpStatusCode';
import Locale from 'localization/Localization';

export class UserService {
	private loginService: LoginService;

	constructor(loginService: LoginService) {
		this.loginService = loginService;
	}

	public async getUser(useSession?: Session): Promise<UserData | undefined> {
		const session = useSession || this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject("Not logged in");

		let headers = { 'Authorization': 'Bearer ' + session.accessToken };
		let init = { method: 'GET', headers: headers };

		let url = Api.getBaseUrl() + 'v2/user';

		const response = await fetch(url, init);
		if (response.status === HttpStatusCode.OK) {
			return response.json().then((json) => {
				const user = json as UserData;
				user.region = Api.getRegion();
				return user;
			});
		}
		else if (response.status === HttpStatusCode.NotFound) {
			return {
				language: Locale.fallback,
				region: Api.getRegion(),
				hasPassword: false
			};
		}

		return Promise.reject("Could not retrieve user");
	}

	public createUser(userData: UserCreateData): Promise<void> {
		const session = this.loginService.getSession();

		let headers: { [key: string]: string };

		if (session !== undefined && typeof session.accessToken === 'string')
			headers = { 'Authorization': 'Bearer ' + session.accessToken };
		else
			headers = { 'X-Client-Id': Api.getClientId() };

		let url = Api.getBaseUrl() + 'v2/user';

		let init = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(userData)
		};

		return fetch(url, init)
			.then((response: Response) => {
				if (response.status === HttpStatusCode.OK)
					return;
				else {
					return response.clone().text().then(returnApiError) as Promise<void>;
				}
			})
			.catch(Promise.reject);
	}

	public modifyUser(userData: UserData): Promise<boolean> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v2/user';

		let init = {
			method: 'PUT',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			},
			body: JSON.stringify(userData)
		};

		return fetch(url, init)
			.then(response => {
				if (response.status === 204) {
					return true;
				}
				else {
					console.error(response.status);
					return false;
				}
			})
			.catch(reason => {
				console.error(reason);
				return false;
			})
	}

	public deleteUser(): Promise<boolean> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v2/user';

		let init = {
			method: 'DELETE',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		return fetch(url, init)
			.then(response => {
				if (response.status === 204) {
					this.loginService.clearSession();
					return true;
				}
				else {
					console.error(response.status)
					return false;
				}
			})
			.catch(reason => {
				console.error(reason);
				return false;
			})
	}
}
